// Dot pagination and optional auto-rotation for the Multicolumn mobile slider.
// Mirrors the page state SliderComponent already tracks instead of
// re-implementing any scroll logic of its own.
class MulticolumnSlider extends HTMLElement {
  connectedCallback() {
    this.sliderComponent = this.closest('slider-component');
    this.slider = this.sliderComponent && this.sliderComponent.querySelector('[id^="Slider-"]');
    if (!this.slider) return;

    this.slides = Array.from(this.slider.querySelectorAll('[id^="Slide-"]'));
    this.dots = Array.from(this.querySelectorAll('.slider-counter__link'));
    this.dots.forEach((dot, index) => dot.addEventListener('click', this.onDotClick.bind(this, index)));
    this.sliderComponent.addEventListener('slideChanged', (event) => this.update(event.detail.currentPage));
    // SliderComponent recalculates totalPages on resize but only announces an
    // actual page change, so the dot count needs its own nudge.
    window.addEventListener('resize', this.update.bind(this, null));
    this.update();

    this.autoplaySpeed = Number(this.slider.dataset.speed || 0) * 1000;
    if (this.slider.dataset.autoplay === 'true' && this.autoplaySpeed > 0) this.setupAutoplay();
  }

  disconnectedCallback() {
    this.pause();
    if (this.observer) this.observer.disconnect();
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  onDotClick(index) {
    const slide = this.slides[index];
    if (!slide) return;
    // An explicit choice means the visitor is driving now.
    this.stop();
    this.slider.scrollTo({ left: slide.offsetLeft - this.slides[0].offsetLeft, behavior: 'smooth' });
  }

  // currentPage is 1-based, matching SliderComponent. Falls back to the
  // component's own value so resize can call this with nothing.
  update(currentPage) {
    const page = currentPage || this.sliderComponent.currentPage || 1;
    // Pages, not slides: with two columns visible out of three there are only two stops.
    const totalPages = this.sliderComponent.totalPages || this.dots.length;

    this.dots.forEach((dot, index) => {
      const isActive = index === page - 1;
      dot.style.display = index < totalPages ? '' : 'none';
      dot.classList.toggle('slider-counter__link--active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  setupAutoplay() {
    // Motion nobody asked for is the first thing to drop when the visitor has
    // said they don't want any.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.section = this.closest('.multicolumn') || this.sliderComponent;

    // Hovering, reading via keyboard, or swiping all mean "leave it alone".
    // Working the carets is the visitor taking over, same as a swipe or a dot.
    this.sliderComponent
      .querySelectorAll('.slider-button')
      .forEach((button) => button.addEventListener('click', this.stop.bind(this)));

    this.section.addEventListener('pointerenter', this.pause.bind(this));
    this.section.addEventListener('pointerleave', this.play.bind(this));
    this.section.addEventListener('focusin', this.pause.bind(this));
    this.section.addEventListener('focusout', this.play.bind(this));
    this.slider.addEventListener('touchstart', this.stop.bind(this), { passive: true });
    document.addEventListener('visibilitychange', () => (document.hidden ? this.pause() : this.play()));

    // Only rotate while the section is actually on screen, so a visitor
    // scrolling past doesn't come back to a row that moved on without them.
    this.visible = false;
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.visible = entry.isIntersecting;
        this.visible ? this.play() : this.pause();
      });
    });
    this.observer.observe(this.section);

    // The observer fires once right after observe(), which on a cold load can
    // land before the stylesheet has settled the slide widths. The track isn't
    // scrollable yet, play() bails, and since the section stays on screen the
    // observer never fires again — so rotation only ever started on a reload.
    // Watching the track for its first real size gives play() a second chance.
    this.resizeObserver = new ResizeObserver(() => {
      if (this.visible) this.play();
    });
    this.resizeObserver.observe(this.slider);
  }

  play() {
    // Idempotent on purpose: the resize observer can call this repeatedly, and
    // restarting the interval each time would keep resetting the countdown so
    // it never actually advanced.
    if (this.stopped || this.autoplay || !this.visible) return;
    // Above 990px the list is a plain grid, so there is nothing to scroll.
    if (this.slider.scrollWidth <= this.slider.clientWidth) return;
    this.slider.setAttribute('aria-live', 'off');
    this.autoplay = setInterval(this.advance.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
    this.autoplay = null;
  }

  // Permanent, unlike pause: once the visitor swipes or picks a dot, rotation
  // stays off for the rest of the visit rather than fighting them.
  stop() {
    this.stopped = true;
    this.pause();
  }

  advance() {
    const lastPage = this.sliderComponent.totalPages || this.slides.length;
    const atEnd = (this.sliderComponent.currentPage || 1) >= lastPage;
    const offset = this.slides.length > 1 ? this.slides[1].offsetLeft - this.slides[0].offsetLeft : 0;

    this.slider.scrollTo({
      left: atEnd ? 0 : this.slider.scrollLeft + offset,
      behavior: 'smooth',
    });
  }
}

customElements.define('multicolumn-slider', MulticolumnSlider);
